import { ErrorMessage, Field, FieldArray, useFormikContext } from 'formik'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import { arrayToObject } from '~/common/arrayUtils2'
import { useMsg } from '~/common/localizationUtils'
import { Formulas, getFormulae } from '~/custom_reports/builder/calculated_fields/Formulae'
import { ReportingDatePicker } from '~/custom_reports/builder/ReportingDatePicker'
import { useCustomReportBuilderStore } from '~/custom_reports/builder/state'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '~/design_system'
import { PlusO, Trash } from '~/icons'
import VndlySelect from '../../../components/VndlySelect'
import { filterOperators, getFilterOperators } from '../../sharedV3'
import { FilterValue } from '../FilterValue'
import { DatasetFieldInput, DatasetInput } from '../FormFields'

const DatePickerTypes = ['date', 'datetime']

Yup.addMethod(Yup.mixed, 'dataTypeMatch', () => {
  return Yup.mixed().test({
    name: 'matches data_type',
    test(value) {
      const data_type = this.from.filter(item => !isEmpty(item.value?.data_type))[0]?.value.data_type

      if (isEmpty(value)) return true

      let pattern = null

      switch (data_type.toLowerCase()) {
        case 'integer':
          pattern = /^\d+$/
          break
        case 'number':
          pattern = /^\d+(\.\d+)?$/
          break

        default:
          pattern = /.*/
          break
      }

      return pattern.test(value)
        ? true
        : this.createError({
            message: `${value} does not match data type ${data_type}`,
            path: this.path
          })
    }
  })
})

export const getCaseFieldValidations = msg => {
  const CaseFieldValidations = {
    fields: Yup.array(
      Yup.object({
        path: Yup.string().required('Source is required').nullable(),
        field: Yup.string().required('Field is required').nullable(),
        operator: Yup.string().required('Operator is required').nullable(),
        value: Yup.string().when('operator', {
          is: operator =>
            operator &&
            filterOperators(msg)
              .flatMap(f => (f.options ? f.options : f))
              .find(f => f.value === operator).dynamic,
          then: Yup.string().nullable(),
          otherwise: Yup.string().required('Value is Required').nullable()
        }),
        then: Yup.string().dataTypeMatch()
      })
    ).min(1, 'At least one value is required'),
    else_is_field: Yup.boolean().optional(),
    else: Yup.mixed().when('else_is_field', {
      is: isField => isField,
      then: Yup.string().nullable(),
      otherwise: Yup.mixed().dataTypeMatch()
    }),
    else_field: Yup.object().when('else_is_field', {
      is: isField => isField,
      then: Yup.object({
        path: Yup.string().required('Source is required').nullable(),
        field: Yup.string().required('Field is required').nullable()
      }).required(),
      otherwise: Yup.object({
        path: Yup.string().nullable(),
        field: Yup.string().nullable()
      }).nullable()
    })
  }
  return CaseFieldValidations
}

const getInputComponent = data_type => {
  if (DatePickerTypes.indexOf(data_type) !== -1) return ReportingDatePicker
  return Input
}

export const Case = ({ calculatedField, setFieldValue }) => {
  const msg = useMsg()
  const actions = useCustomReportBuilderStore(state => state.actions)
  const { values, touched, errors } = useFormikContext()
  const [InputComp, setInputComp] = useState(() => Input)
  const getOperator = operator =>
    filterOperators(msg)
      .flatMap(f => (f.options ? f.options : f))
      .find(f => f.value === operator)
  const isDynamic = operator => getOperator(operator).dynamic === true
  const defaultField = () => ({ path: null, field: null, operator: null, value: null, then: null })

  const resetFields = (fieldPath, fieldsToReset) => {
    fieldsToReset.forEach(inputField => setFieldValue(`${fieldPath}.${inputField}`, null))
  }

  const PermittedFieldPredicateLookup = useMemo(
    () =>
      arrayToObject(
        Formulas,
        f => f,
        f => getFormulae(msg)[f]
      ),
    [msg]
  )

  const PermittedFilterFieldPredicate = useCallback(
    (field, id) =>
      field.filter_type ||
      (PermittedFieldPredicateLookup[field.formula] &&
        !PermittedFieldPredicateLookup[field.formula]?.usageLimitations?.filterFieldDisallowed &&
        field.id !== id),
    [PermittedFieldPredicateLookup]
  )

  const PermittedFieldPredicate = useCallback(
    (field, types, id) =>
      (!types || types.indexOf(field.type) !== -1 || types.indexOf(field.data_type) !== -1) &&
      !PermittedFieldPredicateLookup[field.formula]?.usageLimitations?.formulaFieldDisallowed &&
      field.id !== id,
    [PermittedFieldPredicateLookup]
  )

  const onDatasetChange = (fieldPath, dataset) => {
    setFieldValue(`${fieldPath}.path`, dataset)
    resetFields(fieldPath, ['field', 'operator', 'value', 'then'])
  }

  const onDatasetFieldChange = (fieldPath, field) => {
    setFieldValue(`${fieldPath}.field`, field)
    resetFields(fieldPath, ['operator', 'value', 'then'])
  }

  const onElseIsFieldChange = checked => {
    setFieldValue('else_is_field', checked)
    setFieldValue('else_field', checked ? { path: '', field: '' } : null)
  }

  const onElseDataSourceChange = value => {
    setFieldValue('else_field.path', value)
    setFieldValue('else_field.field', '')
  }

  useEffect(() => {
    // when data_type changes, the else block is a field, and the data types don't match then reset the field
    if (values.else_is_field && values.else_field?.field) {
      const elseFieldType = actions.getFieldType(values.else_field)
      if (values.data_type !== elseFieldType) {
        setFieldValue('else_field.field', '')
      }
    }
    // reset then and else fields if datatype is date or datetime and value isnt a date
    if (['date', 'datetime'].includes(values.data_type)) {
      // eslint-disable-next-line no-unused-expressions
      values?.fields?.forEach((field, index) => {
        if (!moment(field.then).isValid()) setFieldValue(`fields.${index}.then`, '')
      })

      if (!values.else_is_field && !moment(values.else).isValid()) {
        setFieldValue('else', '')
      }
    }
    /*
      eslint-disable-next-line react-hooks/set-state-in-effect --

      ⚠️ HEY YOU, DEV! Yes, YOU! Don't you look away...I know you are reading this. This is your problem now.
      Fix the below while you are working in here. It's simple, just delete this comment, and you'll see the lint error.
      There's even a suggestion on how you might fix it. These particular lint errors are serious issues that we
      couldn't feasibly fix across the whole codebase, but they need fixed. Your help is appreciated! 🥳

      Issue: Deriving inputComp from data_type in effect causes extra render.
      Possible fix approach: Compute inline instead of state. memoization can be handled manually if needed:
        // Remove: const [InputComp, setInputComp] = useState(...)
        // Remove: setInputComp from this useEffect
        const InputComp = getInputComponent(values.data_type)
    */
    setInputComp(() => getInputComponent(values.data_type))
  }, [values.data_type])

  return (
    <Stack spacing={4}>
      <FieldArray
        name="fields"
        render={arrayHelpers => {
          return (
            <Stack>
              <Table
                sx={{
                  'th, td': {
                    px: 1
                  },
                  td: {
                    verticalAlign: 'top'
                  }
                }}>
                <Thead>
                  <Tr>
                    <Th width="22%">Source</Th>
                    <Th width="23%">Field</Th>
                    <Th width="20%">Condition</Th>
                    <Th width="20%">Value</Th>
                    <Th width="15%">Then</Th>
                    <Th width="5%" />
                  </Tr>
                </Thead>
                <Tbody>
                  {calculatedField.fields?.map((field, index) => {
                    const isInvalid = name => touched.fields?.[index]?.[name] && errors.fields?.[index]?.[name]

                    return (
                      <Tr key={field.path || `fields.${index}`}>
                        <Td data-testid={`fields.${index}.path`}>
                          <DatasetInput
                            field={field}
                            name={`fields.${index}.path`}
                            datasetFields
                            calculated
                            systemVariables
                            onChange={datasetOption => onDatasetChange(`fields.${index}`, datasetOption.value)}
                          />
                        </Td>
                        <Td data-testid={`fields.${index}.field`}>
                          <DatasetFieldInput
                            field={field}
                            name={`fields.${index}.field`}
                            predicate={f => PermittedFilterFieldPredicate(f, calculatedField.id)}
                            exclude={[field.id]}
                            onChange={fieldOption => onDatasetFieldChange(`fields.${index}`, fieldOption.value)}
                          />
                        </Td>
                        <Td data-testid={`fields.${index}.operator`}>
                          {field.field && (
                            <FormControl isInvalid={isInvalid('operator')}>
                              <Field
                                name={`fields.${index}.operator`}
                                component={VndlySelect}
                                options={getFilterOperators(
                                  actions.getFieldType(field),
                                  actions.getDatasetField(field),
                                  field.aggregation,
                                  msg
                                )}
                                onChange={o => {
                                  setFieldValue(`fields.${index}.operator`, o.value)
                                  setFieldValue(`fields.${index}.dynamic`, isDynamic(o.value))
                                }}
                              />
                              <FormErrorMessage>
                                <ErrorMessage name={`fields.${index}.operator`} />
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Td>
                        <Td data-testid={`fields.${index}.value`}>
                          {field.field && field.operator && !isDynamic(field.operator) && (
                            <FormControl isInvalid={isInvalid('value')}>
                              <FilterValue
                                name={`fields.${index}.value`}
                                filter={field}
                                setFieldValue={setFieldValue}
                              />
                              <FormErrorMessage>
                                <ErrorMessage name={`fields.${index}.value`} />
                              </FormErrorMessage>
                            </FormControl>
                          )}
                        </Td>
                        <Td data-testid={`fields.${index}.then`}>
                          <FormControl isInvalid={isInvalid('then')}>
                            <Field
                              name={`fields.${index}.then`}
                              placeholder="Then"
                              as={InputComp}
                              setFieldValue={setFieldValue}
                            />
                            <FormErrorMessage>
                              <ErrorMessage name={`fields.${index}.then`} />
                            </FormErrorMessage>
                          </FormControl>
                        </Td>
                        <Td data-testid={`fields.${index}.delete`}>
                          <IconButton
                            type="button"
                            variant="deleteSecondary"
                            icon={<Trash />}
                            aria-label={msg('common.ui.delete')}
                            onClick={() => arrayHelpers.remove(index)}
                          />
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
              <Box>
                <Button type="button" leftIcon={<PlusO />} onClick={() => arrayHelpers.push(defaultField())}>
                  Add Value
                </Button>
              </Box>
            </Stack>
          )
        }}
      />

      <Stack>
        <Heading fontSize="md">Else</Heading>
        <Checkbox
          isChecked={!!calculatedField.else_is_field}
          name="else_is_field"
          onChange={e => onElseIsFieldChange(e.target.checked)}>
          Select Field
        </Checkbox>
        <SimpleGrid columns={2} spacing={4}>
          {calculatedField.else_is_field && (
            <>
              <DatasetInput
                field={calculatedField.else_field}
                name="else_field.path"
                datasetFields
                calculated
                systemVariables
                onChange={datasetOption => onElseDataSourceChange(datasetOption.value)}
              />

              <DatasetFieldInput
                field={calculatedField.else_field}
                predicate={f => PermittedFieldPredicate(f, calculatedField.data_type, calculatedField.id)}
                name="else_field.field"
              />
            </>
          )}
          {!calculatedField.else_is_field && (
            <FormControl isInvalid={touched.else && errors.else}>
              <Field type="text" name="else" as={InputComp} maxLength={50} setFieldValue={setFieldValue} />
              <FormErrorMessage>
                <ErrorMessage name="else" />
              </FormErrorMessage>
            </FormControl>
          )}
        </SimpleGrid>
      </Stack>
    </Stack>
  )
}
